const DEFAULT_PERSISTENCE_READ_TIMEOUT_MS = 300
// Statuses that mean the stored copy is no longer this session's to see; a network error or 5xx is transient.
const STATUSES_THAT_EVICT_PERSISTED_COPY = new Set([401, 403, 404, 410])
const REFRESH_LANDED = Symbol('refresh landed')

export const withTimeout = (promise, ms) => {
  let timer = null
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

const notifyPersistedCopyServed = (persistent, persisted, refresh) => {
  if (!persistent.onPersistedCopyServed) return
  try {
    persistent.onPersistedCopyServed(persisted, refresh)
  } catch (err) {
    // Intentionally ignored: an observer must never fail a request.
  }
}

export class SimpleCache {
  /**
   * @param {object} [options]
   * @param {object} [options.persistence] Optional tier-2 adapter that outlives this JS heap
   *   (IndexedDB). Only callers passing `persistent` to `invoke` touch it, so configuring an
   *   adapter changes nothing for existing callers. Contract:
   *     read(key, scope)                    -> Promise<{ value, timestamp } | null>
   *     write(key, value, timestamp, scope) -> Promise<void> | void
   *     remove(key, scope)                  -> Promise<void> | void
   *   Every adapter call is best-effort: a throw or rejection is treated as a miss.
   * @param {number} [options.persistenceReadTimeoutMs] Max time a tier-2 read may block.
   */
  constructor(options = {}) {
    this.cache = new Map()
    this.persistence = options.persistence || null
    this.persistenceReadTimeoutMs = options.persistenceReadTimeoutMs ?? DEFAULT_PERSISTENCE_READ_TIMEOUT_MS
    this.keysServedFromPersistence = new Set()
  }

  /**
   * @param {Function} func Producer, invoked on a miss.
   * @param {object} options
   * @param {string} options.key
   * @param {number} [options.maxAgeInSeconds] Freshness of the in-memory entry.
   * @param {object} [options.persistent] Opt in to the tier-2 adapter: resolved values are written
   *   through, and the first invoke of a key in this heap resolves the persisted copy (when
   *   present and within `persistent.maxAgeInSeconds`) while `func` refreshes it in the background.
   * @param {*} options.persistent.scope Opaque namespace forwarded to the adapter (identity).
   * @param {number} [options.persistent.maxAgeInSeconds] Max age of a persisted copy that may be served.
   * @param {(servedValue: *, refresh: Promise) => void} [options.persistent.onPersistedCopyServed] Called
   *   when the persisted copy is served, with the background refresh that will replace it.
   */
  async invoke(func, options = {}) {
    const { key: cacheKey, maxAgeInSeconds, persistent } = options
    if (!cacheKey) {
      throw new Error('missing cache key')
    }

    // Check if we have a cached entry
    if (this.cache.has(cacheKey)) {
      const entry = this.cache.get(cacheKey)
      const now = Date.now()
      const isExpired = maxAgeInSeconds !== undefined &&
          now > (entry.timestamp + maxAgeInSeconds * 1000)

      if (!isExpired) {
        return entry.data
      }
    }

    const usePersistence = Boolean(this.persistence && persistent)
    if (usePersistence && !this.keysServedFromPersistence.has(cacheKey)) {
      this.keysServedFromPersistence.add(cacheKey)
      // Held in memory while the store is read, so a concurrent first invoke shares this result.
      const entry = { data: null, timestamp: Date.now() }
      entry.data = this._servePersistedThenRefresh(func, cacheKey, persistent, entry)
      this.cache.set(cacheKey, entry)
      return entry.data
    }

    return this._load(func, cacheKey, usePersistence ? persistent : null)
  }

  async _servePersistedThenRefresh(func, cacheKey, persistent, entry) {
    // Started alongside the read, so a miss or a slow store never delays the request.
    const refresh = this._load(func, cacheKey, persistent, entry)
    refresh.catch(() => {})
    // A fresh value that lands before the read wins; a failed request still waits for the stored copy.
    const landed = refresh.then(() => REFRESH_LANDED, () => new Promise(() => {}))
    const persisted = await Promise.race([this.readPersisted(cacheKey, persistent), landed])
    if (persisted === null || persisted === REFRESH_LANDED) return refresh
    notifyPersistedCopyServed(persistent, persisted, refresh)
    return persisted
  }

  async _load(func, cacheKey, persistent, sharedEntry = null) {
    const startedAt = Date.now()
    const promise = func()
    // The first invoke that reads the store holds its own entry, which resolves the stored copy or this load.
    const entry = sharedEntry || { data: promise, timestamp: startedAt }
    if (!sharedEntry) this.cache.set(cacheKey, entry)

    try {
      const resolved = await promise
      // A purge during the round-trip means this body is already stale.
      if (persistent && this.cache.get(cacheKey) === entry) {
        this._writePersisted(cacheKey, resolved, startedAt, persistent.scope)
      }
      return resolved
    } catch (err) {
      // Remove failed promise from cache
      this.cache.delete(cacheKey)
      if (persistent && STATUSES_THAT_EVICT_PERSISTED_COPY.has(err?.status)) {
        this._removePersisted(cacheKey, persistent.scope)
      }
      throw err
    }
  }

  // Read a live entry WITHOUT invoking the producer — returns the cached promise,
  // or undefined when the key is absent or past maxAgeInSeconds. Callers that only
  // want to inspect what a subsequent `invoke` would hand back (e.g. to decide
  // whether the entry is now stale and must be purged) use this so the inspection
  // itself can never trigger a fetch. The stored value is a promise that may
  // reject, so await it defensively.
  peek(cacheKey, maxAgeInSeconds) {
    if (!this.cache.has(cacheKey)) {
      return undefined
    }
    const entry = this.cache.get(cacheKey)
    const isExpired = maxAgeInSeconds !== undefined &&
        Date.now() > (entry.timestamp + maxAgeInSeconds * 1000)

    return isExpired ? undefined : entry.data
  }

  purge(cacheKey, { scope } = {}) {
    this.cache.delete(cacheKey)
    if (!this.persistence) return
    this._removePersisted(cacheKey, scope)
  }

  _removePersisted(cacheKey, scope) {
    try {
      Promise.resolve(this.persistence.remove(cacheKey, scope)).catch(() => {})
    } catch (err) {
      // Intentionally ignored: a purge must never throw into a teardown path.
    }
  }

  /**
   * Read the tier-2 copy of an entry without touching memory or the producer.
   * Every failure (no adapter, rejection, timeout, bad record, too old) resolves null.
   *
   * @param {string} cacheKey
   * @param {object} options
   * @param {number} [options.maxAgeInSeconds] Max age of the persisted entry.
   * @param {*} [options.scope] Same namespace the entry was written with.
   * @returns {Promise<* | null>}
   */
  async readPersisted(cacheKey, { maxAgeInSeconds, scope } = {}) {
    if (!this.persistence) return null
    try {
      const persisted = await withTimeout(
        Promise.resolve(this.persistence.read(cacheKey, scope)),
        this.persistenceReadTimeoutMs,
      )
      if (!persisted) return null
      const isExpired = maxAgeInSeconds !== undefined &&
          Date.now() > (persisted.timestamp + maxAgeInSeconds * 1000)
      return isExpired ? null : persisted.value
    } catch (err) {
      return null
    }
  }

  _writePersisted(cacheKey, value, timestamp, scope) {
    try {
      Promise.resolve(this.persistence.write(cacheKey, value, timestamp, scope)).catch(() => {})
    } catch (err) {
      // Intentionally ignored: persistence must never fail a request.
    }
  }
}
